import React, { FC, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Context } from "../../index";
import { toast } from "react-toastify";
import { observer } from "mobx-react-lite";
import ButtonComponent from "../buttons/ButtonComponent";
import SVG from "../svg/Svg";
import SquareButton from "../buttons/SquareButton";
import CustomToastContainer from "./notification/CustomToastContainer";

import { getStationDisplayProps } from "../../utils/getStationState";
// `Н49`: номер счётчика — действующей установки, а не поля `station.meter_number`.
import { meterNumberText } from "../../utils/stationMeter";
import { IStation, IModules } from "../models/IUser";
import "./popstyle_station.css";
import { logApiError } from "../../utils/logApiError";

type StationPosition = "list" | "inplants";

interface PopupStationProps {
  station: IStation | null;
  modules: IModules[];
  handleCloseModal: () => void;
  position?: StationPosition;
  updateStation?: (station: Partial<IStation>, position: StationPosition) => Promise<any>;
  restartStation?: (id: number, controller: number) => Promise<any>;
  AvailabilityStation?: (id: number, moduleId: number, controller: number, availability: boolean) => Promise<any>;
  UnlockConnector?: (id: number, controller: number) => Promise<any>;
  refreshData?: () => void;
}

//PopupStation.tsx
const PopupStation: FC<PopupStationProps> = ({
  station,
  modules,
  handleCloseModal,
  position = "list",
  updateStation,
  restartStation,
  AvailabilityStation,
  UnlockConnector,
  refreshData
}) => {
  const { store } = useContext(Context);
  const navigate = useNavigate();

  const foundModule = modules.find(
    (module) => module.id === station?.module_id,
  );
  const identifier = foundModule ? foundModule.charge_id : "";

  const [isEdit, setEdit] = useState(false);

  // 🔥 Получаем актуальные данные для отображения
  const displayProps = getStationDisplayProps(station);

  // --- Функции ---
  async function handleUpdateStation() {
    try {
      if (updateStation) {
        await updateStation(station, position);
      } else {
        toast.error("Функция updateStation не передана");
        return;
      }
      toast.success("Сохранено");
    } catch (error) {
      toast.error(`Ошибка: ${(error as any).message}`);
    }
  }

  async function handleRestartStation(id: number, controller: number) {
    try {
      if (restartStation) {
        const result = await restartStation(id, controller);

        // Успешный ответ от устройства
        if (result?.success && result.status === "accepted") {
          toast.success("Станция успешно перезагружена");
        }
        // `АУД-Н158`: отказ изделия называет причину — `error`, иначе `info`.
        // Прежде отказ попадал в ветку «успех без подтверждения» и читался как
        // «команда ушла, ждите»: оператор не видел отказа.
        else if (result?.status === "rejected") {
          const reason = result.error || result.info;
          toast.error(
            reason ? `Команда отклонена: ${reason}` : "Команда отклонена",
          );
        }
        // Команда отправлена, но нет подтверждения (если поддерживается)
        else if (result?.success) {
          toast.warning("Команда на перезагрузку отправлена, ожидайте...");
        }
        // Неожиданный формат
        else {
          toast.error("Неизвестный ответ от сервера");
        }
      } else {
        toast.error("Функция restartStation не передана");
      }
    } catch (error: any) {
      const errorMessage = error.message || "Не удалось перезагрузить станцию";
      toast.error(`Ошибка: ${errorMessage}`);
      logApiError("RestartStation error:", error);
    }
  }

  async function handleAvailabilityStation(
    id: number,
    moduleId: number,
    controller: number,
    availability: boolean,
  ) {
    try {
      if (AvailabilityStation) {
        const result = await AvailabilityStation(
          id,
          moduleId,
          controller,
          availability,
        );

        // Успех: устройство приняло команду
        if (result?.success && result.status === "accepted") {
          toast.success("Доступность станции успешно изменена");
        }
        // Успех, но устройство не подтвердило (если бэк так возвращает)
        else if (result?.success) {
          toast.warning(
            "Команда отправлена, но устройство не подтвердило изменение",
          );
        }
        // Неожиданный формат ответа
        else {
          toast.error("Неизвестный ответ от сервера");
        }
      } else {
        toast.error("Функция AvailabilityStation не передана");
      }
    } catch (error: any) {
      // Ошибки от бэка или сети
      const errorMessage =
        error.message || "Не удалось изменить доступность станции";
      toast.error(`Ошибка: ${errorMessage}`);
      logApiError("AvailabilityStation error:", error);
    }
  }

  async function handleUnlockConnector(id: number, controller: number) {
    try {
      if (UnlockConnector) {
        const result = await UnlockConnector(id, controller);

        if (result?.success && result.status === "accepted") {
          toast.success("Разъём успешно разблокирован");
        } else if (result?.success) {
          toast.warning("Команда отправлена, но подтверждение не получено");
        } else {
          toast.error("Неизвестный ответ от сервера");
        }
      } else {
        toast.error("Функция UnlockConnector не передана");
      }
    } catch (error: any) {
      const errorMessage = error.message || "Не удалось разблокировать разъём";
      toast.error(`Ошибка: ${errorMessage}`);
      logApiError("UnlockConnector error:", error);
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    if (station) {
      (station as any)[name] = value;
    }
  };

  const handlePlantClick = (id: number, moduleId: number) => {
    const curPlant = store.plants.find((el) => el.id === id);
    if (curPlant) {
      const curOrg = store.companies.find(
        (el) => el.id === curPlant.company_id,
      );
      navigate("/inplants");
    }
    handleCloseModal();
  };
  const errors = station.gps
    ? station.gps.split(",").filter((err) => err.trim() !== "")
    : [];
  return (
    <>
      <div className="popup_station_container">
        {/* === Кнопки сверху === */}
        <div className="popup_station_state">
          <div className="popup_station_btn">
            <ButtonComponent
              title="перезагрузить"
              size="173px"
              svg="restart"
              onClick={() =>
                handleRestartStation(station.module_id, station.controller_address)
              }
            />
            <ButtonComponent
              title="доступность"
              size="173px"
              svg="restart"
              onClick={() =>
                handleAvailabilityStation(
                  station.id,
                  station.module_id,
                  station.controller_address,
                  station.availability,
                )
              }
            />
            <ButtonComponent
              title="коннектор"
              size="173px"
              svg="restart"
              onClick={() =>
                handleUnlockConnector(station.module_id, station.controller_address)
              }
            />
            {/* <ButtonComponent title="запустить" size="173px" svg="start" /> */}
          </div>
          <SquareButton
            onClick={handleCloseModal}
            icon="close"
            background="#48ba67"
            size="24px"
          />
        </div>

        {/* === Статусы === */}
        <div className="popup_station_items">
          {/* Состояние (свободно, заряжается и т.д.) */}
          <div
            className="popup_station_icons"
            style={{ color: displayProps.color }}
          >
            {displayProps.icon}
            {displayProps.state}
          </div>

          {/* `С2`: подсказка под статусом — только у состояния 2 */}
          {displayProps.hint ? (
            <div className="popup_station_hint">{displayProps.hint}</div>
          ) : null}

          {/* Ошибки — всплывающий tooltip */}
          {station.gps ? (
            <div className="popup_station_icons popup_station_errors">
              <div className="error-trigger">
                описание
                <span className="error-count">
                  {errors.length > 0 ? `(${errors.length})` : ""}
                </span>
              </div>

              {/* Tooltip */}
              {errors.length > 0 && (
                <div className="tooltip">
                  <div className="tooltip-content">
                    {errors.map((error, index) => (
                      <div key={index} className="tooltip-row">
                        {error.trim()}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* === Основная информация === */}
        <div className="pluto_container">
          <div className="pluto_block">
            <div className="pluto_big-block_container">
              <div className="pluto_big-block_serial">
                <div>
                  {isEdit ? (
                    <input
                      className="pluto_block_input"
                      name="serial"
                      value={station.serial || ""}
                      onChange={handleChange}
                    />
                  ) : (
                    station.serial
                  )}
                </div>
                <div onClick={() => setEdit(true)}>{SVG["pen_white"]}</div>
              </div>
              <div className="pluto_big-block_IDPLant">
                <div>ID: {station.id}</div>
                <div>{station["Plant.name"]}</div>
              </div>
            </div>
          </div>
          <div className="pluto_plase_block">
            <div className="pluto_plase_block_tittle">МЕСТО УСТАНОВКИ</div>
            {isEdit ? (
              <input
                className="pluto_place_input"
                name="place"
                value={station.place || ""}
                onChange={handleChange}
              />
            ) : (
              <div className="pluto_plase_block_value">{station.place}</div>
            )}
          </div>
        </div>

        {/* === Параметры === */}
        <div className="popup_station_param">
          <div className="popup_big">
            <div className="input-row">
              <div className="input-wrapper">
                <label className="popup_station_label" htmlFor="charge_id">
                  идентификатор модуля
                </label>
                <input
                  className="popup_station_input"
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "#1894c3",
                  }}
                  value={identifier}
                  type="text"
                  id="charge_id"
                  readOnly
                  onClick={() =>
                    handlePlantClick(station.plant_id, foundModule?.id)
                  }
                />
              </div>
              <div className="input-wrapper">
                <label
                  className="popup_station_label"
                  htmlFor="controller_address"
                >
                  адрес контроллера
                </label>
                <input
                  disabled
                  className="popup_station_input"
                  value={station.controller_address}
                  type="text"
                  id="controller_address"
                  name="controller_address"
                />
              </div>
              <div className="input-wrapper">
                <label className="popup_station_label" htmlFor="type">
                  тип
                </label>
                <input
                  disabled
                  className="popup_station_input"
                  value="AC-CHE-M"
                  type="text"
                  id="type"
                  name="type"
                />
              </div>
            </div>
            <div className="input-row">
              <div className="input-wrapper">
                <label className="popup_station_label" htmlFor="software">
                  версия ПО
                </label>
                <input
                  disabled
                  className="popup_station_input"
                  value={station.acVersion}
                  type="text"
                  id="software"
                  name="software"
                />
              </div>
              {/* `Н49`: поля «пользователь станции» здесь больше нет — это был
                  свободный текст о человеке (ПД-03), а связь человека с местом
                  ведёт учётная запись, а не подпись в карточке. */}
              <div className="input-wrapper">
                <label className="popup_station_label" htmlFor="meter_address">
                  номер счетчика
                </label>
                <input
                  disabled
                  className="popup_station_input"
                  // `Н49`: номер — действующей установки (`Meters`), а не
                  // `station.meter_number` (поля нет с 17.10.2025).
                  value={meterNumberText(station)}
                  type="text"
                  id="meter_address"
                  name="meter_address"
                />
              </div>
            </div>
          </div>
          <div className="little">
            <div className="little-wrapper">
              <label className="popup_station_label" htmlFor="description">
                описание
              </label>
              <textarea
                className="popup_big_input"
                value={station.description || "нет описания"}
                onChange={handleChange}
                name="description"
                id="description"
              />
            </div>
          </div>
        </div>

        {/* === Показания === */}
        <div className="popup_station_indicators">
          <div className="popup_station_indicator">
            <div>ТЕКУЩЕЕ ПОТРЕБЛЕНИЕ:</div>
            <div className="popup_station_value">{station.currentA || 0} A</div>
          </div>
          <div className="popup_station_indicator">
            <div>СЧЕТЧИК:</div>
            <div className="popup_station_value">
              {(station.energyWh / 1000).toFixed(2)} кВт·ч
            </div>
          </div>
        </div>

        {/* === Кнопки внизу === */}
        <div className="popup_station_btnblock">
          <ButtonComponent
            onClick={handleUpdateStation}
            title="сохранить"
            size="200px"
          />
        </div>
      </div>
      <CustomToastContainer />
    </>
  );
};

export default observer(PopupStation);
